import { useEffect, useContext, useRef, useLayoutEffect, createRef } from 'react'
import classNames from 'classnames'
import { useSelector, shallowEqual } from 'react-redux'
import PropTypes from 'prop-types'
import { createUseStyles } from 'react-jss'
import gsap from 'gsap'
import { CSSPlugin } from 'gsap/CSSPlugin'
import ally from 'ally.js'
import { Context } from '@/context'
import SimpleSplitText from '@/components/SimpleSplitText'
import DelayLink from '@/components/DelayLink'
import usePrevious from '@/hooks/usePrevious'
import { decodeEntities } from '@/utils/decodeEntities'
import style from './style'

gsap.registerPlugin(CSSPlugin)
const useStyles = createUseStyles(style)

const CollectionNav = ({
  collectionNav,
}) => {
  const { isCollectionNavOpen, setCollectionNavOpen, headerHeight, headerRef, cookieRef } = useContext(Context)
  const classes = useStyles({ headerHeight, isCollectionNavOpen })
  const { pathname } = useSelector((state) => ({
    pathname: state.router.location.pathname,
    mediaQueryKey: state.mediaQuery.key,
    strings: state.language.string,
  }), shallowEqual)
  const prevPathname = usePrevious(pathname)
  const prevCollectionNavOpen = usePrevious(isCollectionNavOpen)

  const $root = useRef()
  const $nav = useRef()
  const $titles = useRef(collectionNav.map(() => createRef()))
  const disableAllTabIndex = useRef(null)

  /*------------------------------
  Initialize
  ------------------------------*/
  const init = () => {
    if ($root.current) gsap.set($root.current, { autoAlpha: 0 })
    if ($nav.current.children) setTimeout(() => gsap.set($nav.current.children, { autoAlpha: 0 }), 500)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && e.keyCode === 27 && isCollectionNavOpen) setCollectionNavOpen(false)
  }

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isCollectionNavOpen])

  const openCollectionMenu = () => {
    const timeline = gsap.timeline({ ease: 'power4.out' })
    if ($root.current) {
      gsap.killTweensOf($root.current)
      timeline.to($root.current, {
        duration: 0.5,
        autoAlpha: 1,
      })
    }
    if ($nav.current.children) {
      gsap.killTweensOf($nav.current.children)
      timeline.to($nav.current.children, {
        stagger: 0.02,
        autoAlpha: 1,
      }, '-=0.3')
    }
  }

  const closeCollectionMenu = () => {
    if ($root.current) {
      gsap.killTweensOf($root.current)
      gsap.to($root.current, {
        duration: 1,
        autoAlpha: 0,
        ease: 'power4.out',
      })
    }
    if ($nav.current.children) {
      gsap.killTweensOf($nav.current.children)
      gsap.to($nav.current.children, {
        duration: 1,
        autoAlpha: 0,
        ease: 'power4.out',
      })
    }
  }

  useEffect(() => {
    if (collectionNav.length > 0) $titles.current = collectionNav.map(() => createRef())
  }, [collectionNav])

  /*------------------------------
  Init
  ------------------------------*/
  useEffect(() => {
    init()
  }, [])

  useEffect(() => {
    if (isCollectionNavOpen) {
      disableAllTabIndex.current = ally.maintain.disabled({
        filter: [$root.current, headerRef.current, ...cookieRef.current ? [cookieRef.current] : []],
      })
    }
    if (!isCollectionNavOpen && disableAllTabIndex.current !== null) disableAllTabIndex.current.disengage()
  }, [isCollectionNavOpen])

  useEffect(() => {
    if (
      prevCollectionNavOpen !== undefined
      && !prevCollectionNavOpen && isCollectionNavOpen
    ) openCollectionMenu()
    if (prevCollectionNavOpen !== undefined
      && prevCollectionNavOpen && !isCollectionNavOpen
    ) closeCollectionMenu()
  }, [isCollectionNavOpen])

  useLayoutEffect(() => {
    if (prevPathname !== pathname) {
      setCollectionNavOpen(false)
    }
  }, [pathname])

  const handleMouseEnter = (i) => $titles.current[i].current.animateIn()
  const handleMouseLeave = (i) => $titles.current[i].current.animateOut()

  const renderCollectionNav = () => {
    return (
      <nav className={classes.collectionNav}>
        <ul ref={$nav}>
          {collectionNav.map((item, i) => (
            <li
              className={classes.navItem}
              onMouseEnter={() => handleMouseEnter(i)}
              onMouseLeave={() => handleMouseLeave(i)}
              key={item.ID}
            >
              <DelayLink
                to={item.url}
                className={classes.navImage}
              >
                <img src={item.description} alt={item.title} />
              </DelayLink>
              <DelayLink
                to={item.url}
                className={classes.navLabel}
              >
                <SimpleSplitText
                  ref={$titles.current[i]}
                  text={decodeEntities(item.title.replace('@', ''))}
                />
              </DelayLink>
            </li>
          ))}
        </ul>
      </nav>
    )
  }

  const handleMouse = (e) => {
    if (e.type === 'mouseenter') setCollectionNavOpen(true)
    if (e.type === 'mouseleave') setCollectionNavOpen(false)
  }

  return (
    <div
      className={classNames({
        [classes.root]: true,
        [classes.isOpen]: isCollectionNavOpen,
      })}
      ref={$root}
      role="dialog"
      aria-modal="true"
      onMouseEnter={handleMouse}
      onMouseLeave={handleMouse}
    >
      <div className={classes.container}>
        <div className={classes.wrapper}>
          {renderCollectionNav()}
        </div>
      </div>
    </div>
  )
}

CollectionNav.propTypes = {
  collectionNav: PropTypes.array,
}

CollectionNav.defaultProps = {
  collectionNav: [],
}

export default CollectionNav
